// ListingDetailsStep.jsx
import React, { useState } from 'react';
import {
  Form,
  Row,
  Col,
  Button,
  Card,
  Badge,
  Spinner,
  Container
} from 'react-bootstrap';
import {
  DollarSign,
  Calendar,
  Clock,
  Briefcase,
  Clipboard,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  ToggleLeft,
  ToggleRight,
  AlertCircle
} from 'lucide-react';
import { useListing } from './contexts/ListingContext';

const ListingDetailsStep = ({
  facilities,
  updateListingField,
  updateClinicalField,
  addListing,
  removeListing,
  errors,
  previewFacilityIndex,
  previewListingIndex,
  setPreviewFacilityIndex,
  setPreviewListingIndex,
  isLoading
}) => {
  // Import all necessary functions from ListingContext
  const { isFetchingAllListings, getListingsForFacility } = useListing();
  const [expandedFacilities, setExpandedFacilities] = useState({});
  const [expandedListings, setExpandedListings] = useState({});
  
  // Local state for checkbox fields to improve UI responsiveness
  const [checkboxState, setCheckboxState] = useState({});
  
  // Helper function to make checkbox changes feel more responsive
  const handleCheckboxChange = (fIdx, lIdx, field, checked) => {
    // Create a unique key for this checkbox
    const key = `${fIdx}-${lIdx}-${field}`;
    
    // Update local state immediately for responsive UI
    setCheckboxState(prevState => ({
      ...prevState,
      [key]: checked
    }));
    
    // Also update through the normal flow
    updateListingField(fIdx, lIdx, field, checked);
  };

  const scheduleTypes = [
    { value: 'Flexible', label: 'Flexible' },
    { value: 'Weekdays', label: 'Weekdays' },
    { value: 'Weekends', label: 'Weekends' },
    { value: 'On-Call Shifts', label: 'On-Call Shifts' }
  ];

  // Must exactly match backend enum values for validation
  const assignmentTypes = [
    { value: 'Full-time', label: 'Full-Time' },
    { value: 'Part-time', label: 'Part-Time' },
    { value: 'Locum tenens', label: 'Locum Tenens' },
    { value: 'PRN', label: 'PRN' },
    { value: 'Permanent', label: 'Permanent' }
  ];

  const caseTypes = [
    { value: 'general', label: 'General' },
    { value: 'pediatric', label: 'Pediatric' },
    { value: 'cardiac', label: 'Cardiac' },
    { value: 'regional', label: 'Regional' },
    { value: 'pain', label: 'Pain' },
    { value: 'critical-care', label: 'Critical Care' }
  ];

  const toggleFacilityExpansion = idx =>
    setExpandedFacilities(prev => ({ ...prev, [idx]: !prev[idx] }));

  // **FIXED**: always set preview indices, then flip expansion
  const toggleListingExpansion = (fIdx, lIdx) => {
    const key = `${fIdx}-${lIdx}`;
    setPreviewFacilityIndex(fIdx);
    setPreviewListingIndex(lIdx);
    setExpandedListings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const listingNeedsFixing = listing =>
    !listing.title || !listing.sourceListingId || !listing.compensation;

  const formatCurrency = (amount, period = 'hourly') =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: period === 'annual' ? 'compact' : undefined,
      maximumFractionDigits: 0
    }).format(amount);

  const getPaySliderConfig = period =>
    period === 'annual'
      ? { min: 150000, max: 800000, step: 10000, displayMin: '150K', displayMax: '800K' }
      : { min: 80, max: 400, step: 5, displayMin: '$80', displayMax: '$400' };

  const handlePayAmountChange = (fIdx, lIdx, value, period) => {
    const val = parseInt(value) || (period === 'annual' ? 150000 : 80);
    const [min, max] = period === 'annual' ? [150000, 800000] : [80, 400];
    const newVal = Math.min(Math.max(val, min), max);
    updateListingField(fIdx, lIdx, 'compensation.amount', newVal);
  };

  const handleSignOnBonusChange = (fIdx, lIdx, field, value) => {
    console.log(`[ListingDetailsStep] handleSignOnBonusChange: fIdx=${fIdx}, lIdx=${lIdx}, field=${field}, value=${value}`);
    // Map to the correct field path according to DTO structure
    const path = field === 'amount' ? 'compensation.signOnBonusAmount' : 'compensation.signOnBonusCommitmentYears';
    
    let num = parseInt(value) || (field === 'amount' ? 25000 : 1);
    if (field === 'amount') num = Math.min(Math.max(num, 25000), 150000);
    else if (field === 'commitment') num = Math.min(Math.max(num, 1), 4);
    
    // Call updateListingField directly to update the UI immediately
    // The ListingContext has debouncing built-in for these fields
    updateListingField(fIdx, lIdx, path, num);
  };

  const toggleSignOnBonus = (fIdx, lIdx, current) => {
    // Debug the current state of signOnBonusOffered
    console.log(`[ListingDetailsStep] toggleSignOnBonus BEFORE: fIdx=${fIdx}, lIdx=${lIdx}, current=${current}`);
    
    // Force current to a boolean to ensure correct toggle behavior
    // Note: We're explicitly handling the value as boolean to avoid string conversion issues
    const currentAsBool = current === true || current === 'true';
    const next = !currentAsBool;
    
    console.log(`[ListingDetailsStep] toggleSignOnBonus AFTER conversion: currentAsBool=${currentAsBool}, next=${next}`);
    
    // In the ListingContext, getListingsForFacility expects a facilityId not an index
    // So we need to get the facilityId from the facilities array first
    if (!facilities || !Array.isArray(facilities) || fIdx >= facilities.length) {
      console.error(`Invalid facilities array or index out of bounds: ${fIdx}`);
      return;
    }
    
    const facility = facilities[fIdx];
    if (!facility) {
      console.error(`Facility not found at index: ${fIdx}`);
      return;
    }
    
    // Get facility ID
    const facilityId = facility.id || facility._id;
    if (!facilityId) {
      console.error(`Facility ID is missing for index: ${fIdx}`);
      return;
    }
    
    // Now get the listings using the facilityId
    const facilityListings = getListingsForFacility(facilityId);
    const listing = facilityListings[lIdx];
    
    if (!listing) {
      console.error(`[toggleSignOnBonus] Cannot find listing at facility ID ${facilityId}, listing ${lIdx}`);
      return;
    }
    
    // Instead of updating fields separately, create a complete compensation object
    // with all sign-on bonus fields updated together
    const currentComp = listing.compensation || {};
    
    // Create a complete compensation object with ALL fields to ensure consistent updates
    const updatedCompensation = {
      // Maintain existing required fields
      amount: currentComp.amount || 80,
      period: currentComp.period || 'hourly',
      contractType: currentComp.contractType || 'W2',
      
      // Optional fields - maintain existing values
      ...(currentComp.includesHousing !== undefined && { includesHousing: currentComp.includesHousing }),
      ...(currentComp.includesTravel !== undefined && { includesTravel: currentComp.includesTravel }),
      ...(currentComp.malpracticeCovered !== undefined && { malpracticeCovered: currentComp.malpracticeCovered }),
      
      // Set ALL sign-on bonus fields together in a single update
      signOnBonusOffered: next,
      signOnBonusAmount: next ? (currentComp.signOnBonusAmount || 25000) : (currentComp.signOnBonusAmount || 0),
      signOnBonusCommitmentYears: next ? (currentComp.signOnBonusCommitmentYears || 2) : (currentComp.signOnBonusCommitmentYears || 0)
    };
    
    console.log(`Updating all sign-on bonus fields in a single update: signOnBonusOffered=${next}`);
    console.log('Full compensation update:', updatedCompensation);
    
    // Update the entire compensation object at once to ensure consistency
    updateListingField(fIdx, lIdx, 'compensation', updatedCompensation);
    
    // Force refresh of the display immediately
    setTimeout(() => {
      // Use the facilityId instead of index to get listings
      const listings = getListingsForFacility(facilityId);
      
      if (listings && listings.length > lIdx) {
        console.log('After toggle - Complete listing:', listings[lIdx]);
        console.log('signOnBonusOffered now:', listings[lIdx]?.compensation?.signOnBonusOffered);
        console.log('signOnBonusAmount now:', listings[lIdx]?.compensation?.signOnBonusAmount);
        console.log('signOnBonusCommitmentYears now:', listings[lIdx]?.compensation?.signOnBonusCommitmentYears);
      } else {
        console.error(`Unable to find listing at index ${lIdx} for facility ID ${facilityId} after update`);
      }
    }, 200);
  };

  const handleWeeklyHoursChange = (fIdx, lIdx, value) => {
    const val = parseInt(value) || 10;
    const newVal = Math.min(Math.max(val, 10), 80);
    updateListingField(fIdx, lIdx, 'schedule.hours', newVal);
  };

  return (
    <Container fluid className="px-0">
      <Row>
        <Col md={12}>
          <div className="form-content">
            <div className="section-title mb-4 d-flex justify-content-between align-items-center">
              <div>
                <Clipboard size={20} className="me-2" />
                Listings Management
              </div>
              <small className="text-muted">
                {facilities.reduce((sum, f) => {
                  const id = f.id || f._id;
                  const list = getListingsForFacility(id) || f.listings || [];
                  return sum + list.length;
                }, 0)} Total Listings
              </small>
            </div>

      {(isLoading || isFetchingAllListings) && (
        <div className="text-center p-3">
          <Spinner animation="border" />
          <p className="mt-2">Loading listings...</p>
        </div>
      )}

      {!isLoading && !isFetchingAllListings && facilities.length === 0 ? (
        <div className="text-center p-5 bg-light rounded">
          <p className="text-muted">Add at least one facility before creating listings.</p>
        </div>
      ) : (
        <div className="facilities-listings-container">
          {facilities.map((facility, fIdx) => {
            const facilityId = facility.id || facility._id;
            const listings = getListingsForFacility(facilityId) || facility.listings || [];
            return (
              <Card key={fIdx} className="facility-card mb-4">
                <Card.Header
                  onClick={() => toggleFacilityExpansion(fIdx)}
                  style={{ cursor: 'pointer' }}
                  className="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <h5 className="mb-0">{facility.name || `Facility ${fIdx + 1}`}</h5>
                    <Badge bg="info" className="ms-2">
                      {listings.length} Listings
                    </Badge>
                  </div>
                  <div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={e => { e.stopPropagation(); addListing(fIdx); }}
                    >
                      <Plus size={16} className="me-1" /> Add
                    </Button>
                    {expandedFacilities[fIdx] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </Card.Header>

                {expandedFacilities[fIdx] && (
                  <Card.Body>
                    {listings.length === 0 ? (
                      <div className="text-center p-4 bg-light rounded">
                        <p className="text-muted">No listings yet.</p>
                        <Button onClick={() => addListing(fIdx)}>
                          <Plus size={18} className="me-1" /> Add Your First Listing
                        </Button>
                      </div>
                    ) : (
                      listings.map((listing, lIdx) => {
                        const key = `${fIdx}-${lIdx}`;
                        const isExpanded = expandedListings[key];
                        const hasErrors = listingNeedsFixing(listing);

                        return (
                          <Card key={key} className={`listing-card mb-3 ${hasErrors ? 'border-warning' : ''}`}>
                            <Card.Header
                              onClick={() => toggleListingExpansion(fIdx, lIdx)}
                              style={{ cursor: 'pointer' }}
                              className={`
                                d-flex justify-content-between align-items-center
                                ${fIdx === previewFacilityIndex && lIdx === previewListingIndex ? 'active-preview' : ''}
                                ${hasErrors ? 'bg-warning-subtle' : ''}
                              `}
                            >
                              <div className="d-flex align-items-center">
                                {hasErrors && <AlertCircle size={18} className="text-warning me-2" />}
                                <div>
                                  <div className="fw-bold">
                                    {listing.title || `Position ${lIdx + 1}`}
                                  </div>
                                  <div className="text-muted small">
                                    {formatCurrency(listing.compensation?.amount || 0, listing.compensation?.period)} – {facility.location || 'TBD'}
                                  </div>
                                </div>
                              </div>
                              <div className="d-flex align-items-center">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  className="me-2"
                                  onClick={e => {
                                    e.stopPropagation();
                                    removeListing(fIdx, lIdx);
                                  }}
                                >
                                  <Trash2 size={16} />
                                </Button>
                                {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                              </div>
                            </Card.Header>

                            {isExpanded && (
                              <Card.Body>
                                {hasErrors && (
                                  <div className="alert alert-warning mb-3">
                                    <h6 className="d-flex align-items-center">
                                      <AlertCircle size={18} className="me-2" />
                                      This listing needs attention
                                    </h6>
                                    <ul className="mb-0 ps-4">
                                      {!listing.title && <li>Missing position title</li>}
                                      {!listing.sourceListingId && <li>Missing listing ID</li>}
                                      {!listing.compensation && <li>Missing compensation details</li>}
                                    </ul>
                                  </div>
                                )}

                                {/* Basic Info */}
                                <div className="mb-4">
                                  <h5 className="section-title d-flex align-items-center">
                                    <Clipboard size={18} className="me-2" />
                                    Basic Information
                                  </h5>
                                  <Row className="mb-3">
                                    <Col md={12}>
                                      <Form.Group>
                                        <Form.Label>Position Title</Form.Label>
                                        <Form.Control
                                          type="text"
                                          value={listing.title || ''}
                                          onChange={e => updateListingField(fIdx, lIdx, 'title', e.target.value)}
                                          isInvalid={!listing.title}
                                        />
                                        {!listing.title && (
                                          <Form.Text className="text-danger">Title is required</Form.Text>
                                        )}
                                      </Form.Group>
                                    </Col>
                                  </Row>
                                </div>

                                {/* Compensation & Contract */}
                                <div className="field-group mb-4">
                                  <h5 className="section-title d-flex align-items-center">
                                    <DollarSign size={18} className="me-2" />
                                    Compensation & Contract
                                  </h5>

                                  <Row className="mb-3">
                                    <Col md={6}>
                                      <Form.Label>Pay Period</Form.Label>
                                      <div className="tag-selector">
                                        {['hourly', 'annual'].map(p => (
                                          <div
                                            key={p}
                                            className={`tag-pill ${listing.compensation?.period === p ? 'selected' : ''}`}
                                            onClick={() => updateListingField(fIdx, lIdx, 'compensation.period', p)}
                                          >
                                            {p.charAt(0).toUpperCase() + p.slice(1)}
                                          </div>
                                        ))}
                                      </div>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Label>Contract Type</Form.Label>
                                      <div className="tag-selector">
                                        {['W2', '1099'].map(ct => (
                                          <div
                                            key={ct}
                                            className={`tag-pill ${listing.compensation?.contractType === ct ? 'selected' : ''}`}
                                            onClick={() => updateListingField(fIdx, lIdx, 'compensation.contractType', ct)}
                                          >
                                            {ct}
                                          </div>
                                        ))}
                                      </div>
                                    </Col>
                                  </Row>

                                  {/* Slider */}
                                  {(() => {
                                    const pr = listing.compensation?.period || 'hourly';
                                    const cfg = getPaySliderConfig(pr);
                                    const amt = listing.compensation?.amount ?? cfg.min;
                                    return (
                                      <div key={pr} className="d-flex flex-column mb-2">
                                        <div className="d-flex justify-content-center mb-2">
                                          <span className="px-3 py-1 rounded-pill bg-gradient" 
                                                style={{
                                                  background: 'linear-gradient(135deg, #8080ff10, #8080ff30)',
                                                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                  border: '1px solid rgba(128,128,255,0.2)',
                                                  fontSize: '0.9rem',
                                                  fontWeight: 'bold'
                                                }}>
                                            {pr === 'hourly' ? '$' + amt : '$' + amt.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                          </span>
                                        </div>
                                        <div className="d-flex align-items-center">
                                          <span className="me-2">{cfg.displayMin}</span>
                                          <Form.Range
                                            min={cfg.min}
                                            max={cfg.max}
                                            step={cfg.step}
                                            value={amt}
                                            onChange={e => handlePayAmountChange(fIdx, lIdx, e.target.value, pr)}
                                            className="flex-grow-1"
                                          />
                                          <span className="ms-2">{cfg.displayMax}</span>
                                        </div>  
                                      </div>
                                    );
                                  })()}

                                  {/* Sign-On Bonus */}
                                  <div className="mt-4">
                                    <div className="d-flex align-items-center mb-2">
                                      <div
                                        onClick={() => toggleSignOnBonus(fIdx, lIdx, listing.compensation?.signOnBonusOffered)}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        {listing.compensation?.signOnBonusOffered
                                          ? <ToggleRight size={24} />
                                          : <ToggleLeft size={24} />
                                        }
                                      </div>
                                      <span className="ms-2">Offer Sign-On Bonus</span>
                                    </div>
                                    {listing.compensation?.signOnBonusOffered && (
                                      <>
                                        <div className="d-flex flex-column mb-3">
                                          <div className="d-flex justify-content-center mb-2">
                                            <span className="px-3 py-1 rounded-pill bg-gradient" 
                                                  style={{
                                                    background: 'linear-gradient(135deg, #8080ff10, #8080ff30)',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                    border: '1px solid rgba(128,128,255,0.2)',
                                                    fontSize: '0.9rem',
                                                    fontWeight: 'bold'
                                                  }}>
                                              ${(listing.compensation?.signOnBonusAmount ?? 25000).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                            </span>
                                          </div>
                                          <div className="d-flex align-items-center">
                                            <span className="me-2">$25K</span>
                                            <Form.Range
                                              min={25000}
                                              max={150000}
                                              step={5000}
                                              value={listing.compensation?.signOnBonusAmount ?? 25000}
                                              onChange={e => handleSignOnBonusChange(fIdx, lIdx, 'amount', e.target.value)}
                                              className="flex-grow-1"
                                            />
                                            <span className="ms-2">$150K</span>
                                          </div>
                                        </div>
                                        <div className="d-flex flex-column">
                                          <div className="d-flex justify-content-center mb-2">
                                            <span className="px-3 py-1 rounded-pill bg-gradient" 
                                                  style={{
                                                    background: 'linear-gradient(135deg, #8080ff10, #8080ff30)',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                    border: '1px solid rgba(128,128,255,0.2)',
                                                    fontSize: '0.9rem',
                                                    fontWeight: 'bold'
                                                  }}>
                                              {listing.compensation?.signOnBonusCommitmentYears ?? 2} {(listing.compensation?.signOnBonusCommitmentYears ?? 2) === 1 ? 'year' : 'years'}
                                            </span>
                                          </div>
                                          <div className="d-flex align-items-center">
                                            <span className="me-2">1 yr</span>
                                            <Form.Range
                                              min={1}
                                              max={4}
                                              step={1}
                                              value={listing.compensation?.signOnBonusCommitmentYears ?? 2}
                                              onChange={e => handleSignOnBonusChange(fIdx, lIdx, 'commitment', e.target.value)}
                                              className="flex-grow-1"
                                            />
                                            <span className="ms-2">4 yrs</span>
                                          </div>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>

                                {/* Assignment Details */}
                                <Form.Group className="field-group mb-4">
                                  <h5 className="section-title d-flex align-items-center">
                                    <Calendar size={18} className="me-2" />
                                    Assignment Details
                                  </h5>
                                  <Row className="mb-3">
                                    <Col md={6}>
                                      <Form.Label>Start Date</Form.Label>
                                      <Form.Control
                                        type="date"
                                        value={listing.assignment?.startDate || ''}
                                        onChange={e => updateListingField(fIdx, lIdx, 'assignment.startDate', e.target.value)}

                                      />
                                    </Col>
                                  </Row>
                                  <Form.Label>Type</Form.Label>
                                  <div className="tag-selector">
                                    {assignmentTypes.map(at => (
                                      <div
                                        key={at.value}
                                        className={`tag-pill ${
                                          // Check both types array and type string for matches
                                          (Array.isArray(listing.assignment?.types) && listing.assignment.types.includes(at.value)) ||
                                          listing.assignment?.type === at.value ||
                                          listing.assignment?.type?.includes(at.value) ? 'selected' : ''
                                        }`}
                                        onClick={() => updateListingField(fIdx, lIdx, 'assignment.type', at.value)}
                                      >
                                        {at.label}
                                      </div>
                                    ))}
                                  </div>
                                </Form.Group>

                                {/* Schedule */}
                                <Form.Group className="field-group mb-4">
                                  <h5 className="section-title d-flex align-items-center">
                                    <Clock size={18} className="me-2" />
                                    Schedule
                                  </h5>
                                  <Form.Label>Type</Form.Label>
                                  <div className="tag-selector mb-3" >
                                    {scheduleTypes.map(st => (
                                      <div
                                        key={st.value}
                                        className={`tag-pill ${(listing.schedule?.pattern === st.value || listing.scheduleType === st.value) ? 'selected' : ''}`}
                                        onClick={() => updateListingField(fIdx, lIdx, 'schedule.type', st.value)}
                                      >
                                        {st.label}
                                      </div>
                                    ))}
                                  </div>
                                  <div className="d-flex flex-column mb-3">
                                    <div className="d-flex justify-content-center mb-2">
                                      <span className="px-3 py-1 rounded-pill bg-gradient" 
                                            style={{
                                              background: 'linear-gradient(135deg, #8080ff10, #8080ff30)',
                                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                              border: '1px solid rgba(128,128,255,0.2)',
                                              fontSize: '0.9rem',
                                              fontWeight: 'bold'
                                            }}>
                                        {listing.estimatedWeeklyHours || listing.schedule?.estimatedWeeklyHours || 0} hours/week
                                      </span>
                                    </div>
                                    <div className="d-flex align-items-center">
                                      <span className="me-2">12</span>
                                      <Form.Range
                                        min={12}
                                        max={80}
                                        step={4}
                                        value={listing.estimatedWeeklyHours || listing.schedule?.estimatedWeeklyHours || 0}
                                        onChange={e => handleWeeklyHoursChange(fIdx, lIdx, e.target.value)}
                                        className="flex-grow-1"
                                      />
                                      <span className="ms-2">80</span>
                                    </div>
                                  </div>
                                  <Row>
                                    <Col md={6}>
                                      <div className="d-flex align-items-center">
                                        <Form.Check
                                          type="checkbox"
                                          id={`onCall-${fIdx}-${lIdx}`}
                                          checked={checkboxState[`${fIdx}-${lIdx}-schedule.onCall`] !== undefined ? 
                                            checkboxState[`${fIdx}-${lIdx}-schedule.onCall`] : listing.schedule?.onCall || false}
                                          onChange={e => handleCheckboxChange(fIdx, lIdx, 'schedule.onCall', e.target.checked)}
                                        />
                                        <Form.Label 
                                          htmlFor={`onCall-${fIdx}-${lIdx}`}
                                          className="ms-2 mb-0 cursor-pointer"
                                          style={{ cursor: 'pointer' }}
                                        >On-Call Required</Form.Label>
                                      </div>
                                    </Col>
                                    <Col md={6}>
                                      <div className="d-flex align-items-center">
                                        <Form.Check
                                          type="checkbox"
                                          id={`weekends-${fIdx}-${lIdx}`}
                                          checked={checkboxState[`${fIdx}-${lIdx}-schedule.weekends`] !== undefined ? 
                                            checkboxState[`${fIdx}-${lIdx}-schedule.weekends`] : listing.schedule?.weekends || false}
                                          onChange={e => handleCheckboxChange(fIdx, lIdx, 'schedule.weekends', e.target.checked)}
                                        />
                                        <Form.Label 
                                          htmlFor={`weekends-${fIdx}-${lIdx}`}
                                          className="ms-2 mb-0 cursor-pointer"
                                          style={{ cursor: 'pointer' }}
                                        >Weekends Required</Form.Label>
                                      </div>
                                    </Col>
                                  </Row>
                                </Form.Group>

                                {/* Clinical Requirements */}
                                <Form.Group className="field-group">
                                  <h5 className="section-title d-flex align-items-center">
                                    <Briefcase size={18} className="me-2" />
                                    Clinical Requirements
                                  </h5>
                                  <Form.Label>Case Types</Form.Label>
                                  <div className="tag-selector mb-3" >
                                    {caseTypes.map(ct => (
                                      <div
                                        key={ct.value}
                                        className={`tag-pill ${
                                          (listing.clinical?.requiredCaseTypes?.includes(ct.value) || 
                                           listing.clinicalRequirements?.requiredCaseTypes?.includes(ct.value)) ? 
                                           'selected' : ''
                                        }`}
                                        onClick={() => updateListingField(fIdx, lIdx, 'case.types', ct.value)}
                                      >
                                        {ct.label}
                                      </div>
                                    ))}
                                  </div>
                                  <div className="d-flex align-items-center mb-2">
                                    <Form.Check
                                      type="checkbox"
                                      id={`housing-${fIdx}-${lIdx}`}
                                      checked={checkboxState[`${fIdx}-${lIdx}-includesHousing`] !== undefined ? 
                                        checkboxState[`${fIdx}-${lIdx}-includesHousing`] : 
                                        listing.includesHousing || listing.compensation?.includesHousing || false}
                                      onChange={e => handleCheckboxChange(fIdx, lIdx, 'includesHousing', e.target.checked)}
                                    />
                                    <Form.Label 
                                      htmlFor={`housing-${fIdx}-${lIdx}`}
                                      className="ms-2 mb-0"
                                      style={{ cursor: 'pointer' }}
                                    >Housing Included</Form.Label>
                                  </div>
                                  
                                  <div className="d-flex align-items-center mb-2">
                                    <Form.Check
                                      type="checkbox"
                                      id={`travel-${fIdx}-${lIdx}`}
                                      checked={checkboxState[`${fIdx}-${lIdx}-includesTravel`] !== undefined ? 
                                        checkboxState[`${fIdx}-${lIdx}-includesTravel`] : 
                                        listing.includesTravel || listing.compensation?.includesTravel || false}
                                      onChange={e => handleCheckboxChange(fIdx, lIdx, 'includesTravel', e.target.checked)}
                                    />
                                    <Form.Label 
                                      htmlFor={`travel-${fIdx}-${lIdx}`}
                                      className="ms-2 mb-0"
                                      style={{ cursor: 'pointer' }}
                                    >Travel Stipend</Form.Label>
                                  </div>
                                  
                                  <div className="d-flex align-items-center">
                                    <Form.Check
                                      type="checkbox"
                                      id={`malpractice-${fIdx}-${lIdx}`}
                                      checked={checkboxState[`${fIdx}-${lIdx}-malpracticeCovered`] !== undefined ? 
                                        checkboxState[`${fIdx}-${lIdx}-malpracticeCovered`] : 
                                        listing.malpracticeCovered !== false || listing.compensation?.malpracticeCovered !== false}
                                      onChange={e => handleCheckboxChange(fIdx, lIdx, 'malpracticeCovered', e.target.checked)}
                                    />
                                    <Form.Label 
                                      htmlFor={`malpractice-${fIdx}-${lIdx}`}
                                      className="ms-2 mb-0"
                                      style={{ cursor: 'pointer' }}
                                    >Malpractice Coverage Included</Form.Label>
                                  </div>
                                </Form.Group>
                              </Card.Body>
                            )}
                          </Card>
                        );
                      })
                    )}
                  </Card.Body>
                )}
              </Card>
            );
          })}
        </div>
      )}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default ListingDetailsStep;
